// core/data/help/safety.ts — /help/safety, authored as data.
//
// How the parts of TruXSocial that act on PEOPLE — rather than on individual
// posts — actually work, including the ones that could be used against you.
// Everything here describes machinery that exists: the trust score's one
// effect, what counts as a mark, the three controls, the appeal path, the
// legal floor.
//
// Blocks are in READING order — the model keeps it (core/models/help.ts).
//
// Public on the web and served to the app by GET /api/help/safety/ — see
// core/data/help/index.ts.

import type { HelpDocument, HelpLine } from '@/core/models/help';

const p = (text: string) => ({ kind: 'paragraph' as const, runs: [{ text }] });
const bullets = (items: HelpLine[]) => ({ kind: 'bullets' as const, items });

export const helpSafety: HelpDocument = {
  id: 'safety',
  title: 'Trust & Safety',
  lead: 'How the parts of TruXSocial that act on people (rather than on individual posts) actually work, including the ones that could be used against you.',
  sections: [
    {
      id: 'trust-score',
      heading: 'Your trust score changes how much your reports weigh. Nothing else.',
      blocks: [
        p(
          'Reporting accurately raises it; reporting things that Council teams repeatedly disagree with lowers it. A higher score means your reports count for more toward opening a case.',
        ),
        p(
          'It does not affect what you can post, who can see you, how far your posts travel, or how your own content is judged. A low score does not make you easier to remove, and a high one does not protect you.',
        ),
      ],
    },
    {
      id: 'being-reported',
      heading: 'Being reported is not a mark against you',
      blocks: [
        p(
          'Anyone can report anything. That is why reports have to accumulate and agree before anything happens, and why a case decided in your favour leaves no record against you.',
        ),
        p(
          'What does count as a real mark is narrow: a suspension or ban, or a Council decision that actually removed your post or actioned your account.',
        ),
      ],
    },
    {
      id: 'what-you-control',
      heading: 'What you control',
      blocks: [
        bullets([
          [
            {
              text: 'Your region is private by default. It is only shown on your profile if you switch it on, in ',
            },
            { label: 'Language & Region', href: '/settings/language' },
            { text: '.' },
          ],
          [
            { text: 'Who can message or mention you, and whether your account is private, in ' },
            { label: 'Privacy', href: '/settings/privacy' },
            { text: '.' },
          ],
          [
            { text: 'Your data. You can download everything we hold about you from ' },
            { label: 'Account settings', href: '/settings/account' },
            { text: '.' },
          ],
        ]),
      ],
    },
    {
      id: 'if-a-decision-goes-against-you',
      heading: 'If a decision goes against you',
      blocks: [
        p(
          'Every decision arrives with a written explanation and the real vote. You can appeal, and you say why. That explanation is the first thing the fresh team reads. The team that hears an appeal never includes anyone who made the original decision.',
        ),
        p(
          'If you were suspended or banned rather than having a post removed, that is appealed separately, from the sign-in screen.',
        ),
      ],
    },
    {
      id: 'legal-floor',
      heading: 'The legal floor',
      blocks: [
        p(
          'A short, closed list of material is unlawful to host. It sits outside the Council entirely: not judged, not voted on, and one report is enough. Keeping it separate is what stops “exactly five categories” from quietly becoming six.',
        ),
        {
          kind: 'callout',
          runs: [
            { label: 'Report illegal content', href: '/report-illegal' },
            { text: ': no account needed, and you do not need to be the person affected.' },
          ],
        },
      ],
    },
    {
      id: 'emergency',
      heading: 'In an emergency',
      blocks: [
        p(
          'If someone is in immediate danger, contact your local emergency services. We are not an emergency service and cannot act at that speed. Reporting here as well is still worth doing, but it is not a substitute.',
        ),
      ],
    },
  ],
};
